import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import { MultiPolygon, Polygon } from 'ol/geom';
import type Geometry from 'ol/geom/Geometry';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorImageLayer from 'ol/layer/VectorImage';
import OSM from 'ol/source/OSM';
import XYZ from 'ol/source/XYZ';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style } from 'ol/style';
import type BaseLayer from 'ol/layer/Base';
import type { Extent } from 'ol/extent';
import { transform } from 'ol/proj';

import { CANVAS_COLOR_VARS, getCanvasColor } from '../canvas-theme.utils';

/** Marks layers that are removable base maps (not user drawings). */
export const CV_BASEMAP_KEY = 'cvBasemap';

/** Marks the administrative boundary overlay (not a basemap). */
export const CV_MAHARASHTRA_BOUNDARY_KEY = 'cvMaharashtraBoundary';

/** Marks the outside-boundary dim overlay (not a basemap). */
export const CV_MAHARASHTRA_DIM_KEY = 'cvMaharashtraDim';

/** Semi-transparent fill over the map outside the project boundary (EPSG:4326 mask). */
function outsideMaharashtraDimStyle(): Style {
  return new Style({
    fill: new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.maskFill) }),
  });
}

/** World-spanning outer ring for the inverse mask polygon. */
const WORLD_MASK_OUTER_RING: number[][] = [
  [-180, -85],
  [180, -85],
  [180, 85],
  [-180, 85],
  [-180, -85],
];

function tagBasemap<T extends BaseLayer>(layer: T): T {
  layer.set(CV_BASEMAP_KEY, true);
  return layer;
}

function readBoundaryFeatures(geojson: object, mapProjection: string): VectorSource {
  const format = new GeoJSON();
  return new VectorSource({
    features: format.readFeatures(geojson, {
      featureProjection: mapProjection,
      dataProjection: 'EPSG:4326',
    }),
  });
}

/** Per-URL cache so different apps (or tests) each get their own fetch promise. */
const boundaryGeoJsonCache = new Map<string, Promise<object>>();

async function fetchBoundaryGeoJson(boundaryGeoJsonUrl: string): Promise<object> {
  let cached = boundaryGeoJsonCache.get(boundaryGeoJsonUrl);
  if (!cached) {
    cached = (async () => {
      const res = await fetch(boundaryGeoJsonUrl);
      if (!res.ok) {
        throw new Error(
          `Failed to load boundary GeoJSON from "${boundaryGeoJsonUrl}" (HTTP ${res.status}). ` +
            `Ensure the file is served at this URL and the CANVAS_BOUNDARY_GEOJSON_URL token is correct.`,
        );
      }
      return (await res.json()) as object;
    })();
    boundaryGeoJsonCache.set(boundaryGeoJsonUrl, cached);
  }
  return cached;
}

async function loadBoundarySource(
  boundaryGeoJsonUrl: string,
  mapProjection = 'EPSG:4326',
): Promise<VectorSource> {
  const geojson = await fetchBoundaryGeoJson(boundaryGeoJsonUrl);
  return readBoundaryFeatures(geojson, mapProjection);
}

/** Builds polygon rings: world outer + boundary cut-outs (and interior lake rings). */
function buildOutsideMaharashtraMaskRings(
  features: Feature<Geometry>[],
  mapProjection = 'EPSG:4326',
): number[][][] {
  const worldRing =
    mapProjection === 'EPSG:4326'
      ? WORLD_MASK_OUTER_RING
      : WORLD_MASK_OUTER_RING.map((coord) =>
          transform(coord, 'EPSG:4326', mapProjection),
        );
  const rings: number[][][] = [worldRing];
  for (const feat of features) {
    const geom = feat.getGeometry();
    if (!geom) continue;
    if (geom instanceof MultiPolygon) {
      for (const poly of geom.getPolygons()) {
        appendPolygonMaskRings(poly.getCoordinates(), rings);
      }
    } else if (geom instanceof Polygon) {
      appendPolygonMaskRings(geom.getCoordinates(), rings);
    }
  }
  return rings;
}

function appendPolygonMaskRings(coords: number[][][], rings: number[][][]): void {
  if (!coords[0]?.length) return;
  rings.push(coords[0]);
  for (let i = 1; i < coords.length; i++) {
    if (coords[i]?.length) {
      rings.push(coords[i]);
    }
  }
}

/** Maharashtra state extent in EPSG:4326 (from division polygons). */
export const MAHARASHTRA_MAP_EXTENT: Extent = [
  72.64292983978679, 15.606793620831443, 80.89955759435033, 22.029028358855232,
];

/** India state boundaries extent in EPSG:4326. */
export const INDIA_MAP_EXTENT: Extent = [
  68.18624878615076, 6.754255805295052, 97.41162872664322, 37.078268147903735,
];

function divisionBoundaryStyle(): Style {
  return new Style({
    stroke: new Stroke({ color: getCanvasColor(CANVAS_COLOR_VARS.boundaryStroke), width: 1.2 }),
    fill: new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.boundaryFill) }),
  });
}

function divisionBoundaryStyleDark(): Style {
  return new Style({
    stroke: new Stroke({ color: getCanvasColor(CANVAS_COLOR_VARS.boundaryStrokeDark), width: 1.1 }),
    fill: new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.boundaryFillDark) }),
  });
}

/**
 * Vector overlay for the project boundary divisions.
 * Shown above raster basemaps; not removed when switching base layers.
 *
 * @param boundaryGeoJsonUrl Full browser URL to the project-specific boundary GeoJSON.
 *   Provide via the `CANVAS_BOUNDARY_GEOJSON_URL` injection token.
 */
export async function createMaharashtraBoundaryLayer(
  boundaryGeoJsonUrl: string,
  mapProjection = 'EPSG:4326',
): Promise<VectorLayer<VectorSource>> {
  const source = await loadBoundarySource(boundaryGeoJsonUrl, mapProjection);
  const layer = new VectorLayer({
    source,
    style: divisionBoundaryStyle(),
    declutter: false,
  });
  layer.set(CV_MAHARASHTRA_BOUNDARY_KEY, true);
  return layer;
}

/**
 * Dims everything outside the project boundary while leaving the region at full basemap opacity.
 * Sits above raster basemaps and below the boundary stroke overlay.
 *
 * @param boundaryGeoJsonUrl Full browser URL to the project-specific boundary GeoJSON.
 *   Provide via the `CANVAS_BOUNDARY_GEOJSON_URL` injection token.
 */
export async function createMaharashtraOutsideDimLayer(
  boundaryGeoJsonUrl: string,
  mapProjection = 'EPSG:4326',
): Promise<VectorImageLayer<VectorSource>> {
  const boundarySource = await loadBoundarySource(boundaryGeoJsonUrl, mapProjection);
  const maskFeature = new Feature(
    new Polygon(
      buildOutsideMaharashtraMaskRings(boundarySource.getFeatures(), mapProjection),
    ),
  );
  const layer = new VectorImageLayer({
    source: new VectorSource({ features: [maskFeature] }),
    style: outsideMaharashtraDimStyle(),
  });
  layer.set(CV_MAHARASHTRA_DIM_KEY, true);
  return layer;
}

export function createStandardOsmLayer(): TileLayer<OSM> {
  return tagBasemap(
    new TileLayer({
      source: new OSM({ crossOrigin: 'anonymous' }),
    }),
  );
}

export function createHumanitarianBasemapLayer(): TileLayer<OSM> {
  return tagBasemap(
    new TileLayer({
      source: new OSM({
        url: 'https://{a-c}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png',
        attributions: '© OpenStreetMap contributors, © HOT',
        crossOrigin: 'anonymous',
      }),
    }),
  );
}

export function createTerrainBasemapLayer(): TileLayer<OSM> {
  return tagBasemap(
    new TileLayer({
      source: new OSM({
        url: 'https://{a-c}.tile.opentopomap.org/{z}/{x}/{y}.png',
        attributions: '© OpenStreetMap contributors, © OpenTopoMap',
        crossOrigin: 'anonymous',
      }),
    }),
  );
}

/** WebMercator tile grid from an OGC API Features map tiles endpoint (ui-canvas parity). */
export function createOpenSeriesBasemapLayer(ogcBaseUrl: string, collectionId: string): TileLayer<XYZ> {
  const base = ogcBaseUrl.replace(/\/+$/, '');
  return tagBasemap(
    new TileLayer({
      source: new XYZ({
        url: `${base}/collections/${collectionId}/map/tiles/WebMercatorQuad/{z}/{x}/{-y}`,
        attributions: '',
        crossOrigin: 'anonymous',
      }),
    }),
  );
}

/**
 * "Basic light" — boundary divisions on light fills.
 *
 * @param boundaryGeoJsonUrl Full browser URL to the project-specific boundary GeoJSON.
 *   Provide via the `CANVAS_BOUNDARY_GEOJSON_URL` injection token.
 */
export async function createBasicLightBasemapLayer(
  boundaryGeoJsonUrl: string,
): Promise<VectorImageLayer<VectorSource>> {
  const source = await loadBoundarySource(boundaryGeoJsonUrl);
  return tagBasemap(
    new VectorImageLayer({
      source,
      style: new Style({
        stroke: new Stroke({ color: getCanvasColor(CANVAS_COLOR_VARS.basemapLightStroke), width: 0.6 }),
        fill: new Fill({ color: getCanvasColor(CANVAS_COLOR_VARS.basemapLightFill) }),
      }),
      declutter: true,
    }),
  );
}

/**
 * "Basic dark" — boundary divisions on dark background.
 *
 * @param boundaryGeoJsonUrl Full browser URL to the project-specific boundary GeoJSON.
 *   Provide via the `CANVAS_BOUNDARY_GEOJSON_URL` injection token.
 */
export async function createBasicDarkBasemapLayer(
  boundaryGeoJsonUrl: string,
): Promise<VectorLayer<VectorSource>> {
  const source = await loadBoundarySource(boundaryGeoJsonUrl);
  return tagBasemap(
    new VectorLayer({
      source,
      style: divisionBoundaryStyleDark(),
      declutter: true,
      background: getCanvasColor(CANVAS_COLOR_VARS.basemapDarkBg),
    }),
  );
}
